import {
  type AutocompleteRenderInputParams,
  type AutocompleteValue,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Autocomplete as MUIAutocomplete,
  type AutocompleteProps as MUIAutocompleteProps,
  Popper,
  type PopperProps,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { includes } from 'lodash';
import { type ReactNode, useCallback } from 'react';
import { pxToRem } from 'utils/getFontValue';
import { ClearIcon } from './icons/ClearIcon';
import { SearchIconMUI } from './icons/SearchIconMUI';
import SearchNotFound from './SearchNotFound';

const AutocompleteChip = styled(Chip)({
  display: 'flex',
  fontSize: 'large',
  justifyContent: 'center',
});

const PopperStyle = styled((props: PopperProps) => <Popper placement="bottom-start" {...props} />)({
  width: '280px !important',
});

export type AutocompleteInputParamsProps = {
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  label?: string;
  sx?: TextFieldProps['sx'];
};

export type AutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<
  MUIAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
  'renderInput' | 'onChange' | 'noOptionsText' | 'onInputChange' | 'renderOption'
> & {
  inputParams: AutocompleteInputParamsProps;
  isSearchFilteringHandledExternally?: boolean; // true if the caller of this component filters the options itself
  renderOption?: (value: Value) => ReactNode;
  onInputChange?: (value: string) => void;
  onChange: (value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>) => void;
  shouldRenderTags?: boolean;
  optionNotFoundComponent?: ReactNode;
};

export const Autocomplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  inputParams,
  onChange,
  shouldRenderTags,
  renderOption,
  onInputChange,
  isSearchFilteringHandledExternally,
  filterOptions,
  sx,
  optionNotFoundComponent,
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) => {
  type ActualComponentProps = MUIAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>;

  const { multiple, getOptionLabel, value } = props;
  const showSearchIcon = !shouldRenderTags;
  const showClearButton = !shouldRenderTags && (Array.isArray(value) ? value.length > 0 : !!value);
  const { required, placeholder, helperText, label, error, sx: sxInputParams } = inputParams;

  const actualRenderInput: ActualComponentProps['renderInput'] = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        required={required}
        placeholder={placeholder}
        helperText={helperText}
        label={label}
        error={error}
        InputProps={{
          ...params.InputProps,
          sx: sxInputParams,
          style: {
            paddingRight: '35px',
            ...(params.size === 'small' ? { fontSize: pxToRem(14) } : {}),
          },
          ...(showSearchIcon && {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIconMUI color="disabled" sx={{ ml: '5px' }} />
              </InputAdornment>
            ),
          }),
          ...(showClearButton && {
            endAdornment: !props.disableClearable && !props.disabled && (
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                size="small"
                onClick={() => {
                  // biome-ignore lint/suspicious/noExplicitAny: FIXME - the any is wrong here
                  onChange((props.multiple ? [] : null) as any);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }),
        }}
        inputProps={params.inputProps}
      />
    ),
    [
      required,
      placeholder,
      helperText,
      label,
      error,
      sxInputParams,
      showSearchIcon,
      showClearButton,
      props.disableClearable,
      props.disabled,
      props.multiple,
      onChange,
    ]
  );

  const actualOnInputChange: ActualComponentProps['onInputChange'] =
    onInputChange &&
    ((e, newValue) => {
      // Prevent onInputChange call when user select an option
      if (e && (e.type === 'change' || includes(String(e.target), 'SVG'))) onInputChange(newValue);
    });

  const actualOnChange: ActualComponentProps['onChange'] = (_, value) => onChange(value);

  const actualRenderTags: ActualComponentProps['renderTags'] = shouldRenderTags
    ? (tagValue, getTagProps) => {
        return tagValue.map((option, index) => (
          // biome-ignore lint/correctness/useJsxKeyInIterable: key is provided by getTagProps
          <AutocompleteChip
            {...getTagProps({ index })}
            label={getOptionLabel ? getOptionLabel(option) : ''}
            color="primary"
          />
        ));
      }
    : () => undefined;

  const actualRenderOption: ActualComponentProps['renderOption'] = (prps, option, state) => (
    <li {...prps} key={state.index}>
      {multiple && <Checkbox checked={state.selected} size="small" />}
      {renderOption ? renderOption(option) : getOptionLabel ? getOptionLabel(option) : ''}
    </li>
  );

  const actualFilterOptions: ActualComponentProps['filterOptions'] =
    isSearchFilteringHandledExternally ? (options, _) => options : filterOptions;

  return (
    <MUIAutocomplete
      // defaults props
      disableCloseOnSelect={multiple}
      popupIcon={null}
      PopperComponent={PopperStyle}
      noOptionsText={optionNotFoundComponent || <SearchNotFound />}
      size="medium"
      fullWidth
      autoHighlight
      // wrapped props
      renderInput={actualRenderInput}
      onInputChange={actualOnInputChange}
      onChange={actualOnChange}
      renderTags={actualRenderTags}
      renderOption={actualRenderOption}
      filterOptions={actualFilterOptions}
      sx={{
        '& button.MuiAutocomplete-clearIndicator': {
          visibility: 'visible',
        },
        ...sx,
      }}
      clearIcon={null}
      // others
      {...props}
    />
  );
};
